// ======================================================================
// FILE: src/modules/wms/calendar/components/CalendarShiftsTab.js
// MODULE: Work Management System (WMS)
// FEATURE: Calendar Management
// COMPONENT: Shifts Tab
//
// PURPOSE:
//   Configure Shift Definitions
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import wmsCalendarService
from "../../services/wmsCalendarService";

const DEFAULT_FORM = {

  shift_code: "",
  shift_name: "",

  start_time: "09:00:00",
  end_time: "18:00:00",

  break_minutes: 60,

  is_default_shift: false,
  is_overnight_shift: false,

  description: "",
  remarks: "",
};

export default function CalendarShiftsTab({
  calendarId,
}) {

  const [loading, setLoading] =
    useState(false);

  const [saving, setSaving] =
    useState(false);

  const [rows, setRows] =
    useState([]);

  const [showForm, setShowForm] =
    useState(false);

  const [editingId, setEditingId] =
    useState(null);

  const [form, setForm] =
    useState(DEFAULT_FORM);

  // ==========================================================
  // LOAD
  // ==========================================================

  async function loadData() {

    if (!calendarId) {
      return;
    }

    try {

      setLoading(true);

      const data =
        await wmsCalendarService
          .getShifts(calendarId);

      setRows(data || []);

    } catch (err) {

      console.error(err);

      alert(
        err?.response?.data?.message
        || "Failed to load shifts"
      );

    } finally {

      setLoading(false);
    }
  }

  useEffect(() => {

    loadData();

  }, [calendarId]);

  // ==========================================================
  // NEW
  // ==========================================================

  function handleAdd() {

    setEditingId(null);

    setForm(
      DEFAULT_FORM
    );

    setShowForm(true);
  }

  // ==========================================================
  // EDIT
  // ==========================================================

  function handleEdit(row) {

    setEditingId(
      row.id
    );

    setForm({

      shift_code:
        row.shift_code || "",

      shift_name:
        row.shift_name || "",

      start_time:
        row.start_time || "",

      end_time:
        row.end_time || "",

      break_minutes:
        row.break_minutes || 0,

      is_default_shift:
        row.is_default_shift || false,

      is_overnight_shift:
        row.is_overnight_shift || false,

      description:
        row.description || "",

      remarks:
        row.remarks || "",
    });

    setShowForm(true);
  }

  // ==========================================================
  // SAVE
  // ==========================================================

  async function handleSave() {

    if (!form.shift_code) {

      alert(
        "Shift Code is required"
      );

      return;
    }

    if (!form.shift_name) {

      alert(
        "Shift Name is required"
      );

      return;
    }

    try {

      setSaving(true);

      if (editingId) {

        await wmsCalendarService
          .updateShift(
            calendarId,
            editingId,
            form
          );

      } else {

        await wmsCalendarService
          .createShift(
            calendarId,
            form
          );
      }

      setShowForm(false);

      await loadData();

    } catch (err) {

      console.error(err);

      alert(
        err?.response?.data?.message
        || "Failed to save shift"
      );

    } finally {

      setSaving(false);
    }
  }

  // ==========================================================
  // DELETE
  // ==========================================================

  async function handleDelete(row) {

    const ok = window.confirm(
      `Delete Shift ${row.shift_name}?`
    );

    if (!ok) return;

    try {

      await wmsCalendarService
        .deleteShift(
          calendarId,
          row.id
        );

      await loadData();

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Delete failed"
      );
    }
  }

  // ==========================================================
  // UI
  // ==========================================================

  if (!calendarId) {

    return (

      <div className="alert alert-info">

        Save calendar first
        before configuring shifts.

      </div>
    );
  }

  return (

    <div>

      {/* ==================================================== */}
      {/* HEADER */}
      {/* ==================================================== */}

      <div
        className="d-flex justify-content-between align-items-center mb-3"
      >

        <h5>
          Shift Configuration
        </h5>

        <button
          className="btn btn-primary"
          onClick={handleAdd}
        >
          Add Shift
        </button>

      </div>

      {/* ==================================================== */}
      {/* FORM */}
      {/* ==================================================== */}

      {showForm && (

        <div className="card mb-3">

          <div className="card-header">

            {
              editingId
                ? "Edit Shift"
                : "Add Shift"
            }

          </div>

          <div className="card-body">

            <div className="row">

              <div className="col-md-3">

                <label>
                  Shift Code
                </label>

                <input
                  className="form-control"
                  value={form.shift_code}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      shift_code:
                        e.target.value,
                    })
                  }
                />

              </div>

              <div className="col-md-3">

                <label>
                  Shift Name
                </label>

                <input
                  className="form-control"
                  value={form.shift_name}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      shift_name:
                        e.target.value,
                    })
                  }
                />

              </div>

              <div className="col-md-2">

                <label>
                  Start Time
                </label>

                <input
                  type="time"
                  className="form-control"
                  value={
                    form.start_time
                      ?.substring(0, 5)
                  }
                  onChange={(e) =>
                    setForm({
                      ...form,
                      start_time:
                        e.target.value + ":00",
                    })
                  }
                />

              </div>

              <div className="col-md-2">

                <label>
                  End Time
                </label>

                <input
                  type="time"
                  className="form-control"
                  value={
                    form.end_time
                      ?.substring(0, 5)
                  }
                  onChange={(e) =>
                    setForm({
                      ...form,
                      end_time:
                        e.target.value + ":00",
                    })
                  }
                />

              </div>

              <div className="col-md-2">

                <label>
                  Break
                </label>

                <input
                  type="number"
                  className="form-control"
                  value={
                    form.break_minutes
                  }
                  onChange={(e) =>
                    setForm({
                      ...form,
                      break_minutes:
                        e.target.value,
                    })
                  }
                />

              </div>

            </div>

            <div className="row mt-3">

              <div className="col-md-3">

                <div className="form-check">

                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={
                      form.is_default_shift
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        is_default_shift:
                          e.target.checked,
                      })
                    }
                  />

                  <label
                    className="form-check-label"
                  >
                    Default Shift
                  </label>

                </div>

              </div>

              <div className="col-md-3">

                <div className="form-check">

                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={
                      form.is_overnight_shift
                    }
                    onChange={(e) =>
                      setForm({
                        ...form,
                        is_overnight_shift:
                          e.target.checked,
                      })
                    }
                  />

                  <label
                    className="form-check-label"
                  >
                    Overnight Shift
                  </label>

                </div>

              </div>

            </div>

            <div className="mt-3">

              <button
                className="btn btn-primary me-2"
                onClick={handleSave}
                disabled={saving}
              >
                {
                  saving
                    ? "Saving..."
                    : "Save"
                }
              </button>

              <button
                className="btn btn-secondary"
                onClick={() =>
                  setShowForm(false)
                }
              >
                Cancel
              </button>

            </div>

          </div>

        </div>

      )}

      {/* ==================================================== */}
      {/* GRID */}
      {/* ==================================================== */}

      <div className="card">

        <div className="card-body p-0">

          <table
            className="table table-bordered table-hover mb-0"
          >

            <thead>

              <tr>

                <th>
                  Code
                </th>

                <th>
                  Name
                </th>

                <th>
                  Start
                </th>

                <th>
                  End
                </th>

                <th>
                  Break
                </th>

                <th>
                  Default
                </th>

                <th>
                  Overnight
                </th>

                <th width="140">
                  Actions
                </th>

              </tr>

            </thead>

            <tbody>

              {loading && (

                <tr>

                  <td
                    colSpan="8"
                    className="text-center"
                  >
                    Loading...
                  </td>

                </tr>

              )}

              {!loading &&
                rows.length === 0 && (

                <tr>

                  <td
                    colSpan="8"
                    className="text-center"
                  >
                    No Shifts Found
                  </td>

                </tr>

              )}

              {!loading &&
                rows.map((row) => (

                <tr key={row.id}>

                  <td>
                    {row.shift_code}
                  </td>

                  <td>
                    {row.shift_name}
                  </td>

                  <td>
                    {row.start_time}
                  </td>

                  <td>
                    {row.end_time}
                  </td>

                  <td>
                    {row.break_minutes}
                  </td>

                  <td>
                    {
                      row.is_default_shift
                        ? "Yes"
                        : "No"
                    }
                  </td>

                  <td>
                    {
                      row.is_overnight_shift
                        ? "Yes"
                        : "No"
                    }
                  </td>

                  <td>

                    <button
                      className="btn btn-sm btn-warning me-1"
                      onClick={() =>
                        handleEdit(row)
                      }
                    >
                      Edit
                    </button>

                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() =>
                        handleDelete(row)
                      }
                    >
                      Delete
                    </button>

                  </td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

      </div>

    </div>
  );
}